import { Check, type LucideIcon } from 'lucide-react'

import { Progress } from '@/components/ui/progress'
import { cn } from '../../../lib/utils'
import type { InsuranceApplicationProgress as ProgressStep } from '../../../models/insuranceApplication'

export type ApplicationStep = ProgressStep & {
  /** Stands in for the status dot, where a step is worth marking out. */
  icon?: LucideIcon
}

/** The step being worked on; with none in progress, the next locked one, else the last. */
function currentStepIndex(steps: ReadonlyArray<ApplicationStep>) {
  const inProgress = steps.findIndex((step) => step.status === 'IN_PROGRESS')
  if (inProgress !== -1) return inProgress
  const locked = steps.findIndex((step) => step.status === 'LOCKED')
  return locked !== -1 ? locked : steps.length - 1
}

function StepMarker({ step }: { step: ApplicationStep }) {
  if (step.icon) return <step.icon className="text-ink-300 size-4" />
  if (step.status === 'COMPLETED') return <Check className="text-ink size-4" />
  return (
    <span className={cn('size-1.5 rounded-full', step.status === 'IN_PROGRESS' ? 'bg-ink' : 'bg-ink-300')} />
  )
}

/** The application's steps, down the side of the page. */
export function InsuranceApplicationProgress({
  steps,
  className,
}: {
  steps: ReadonlyArray<ApplicationStep>
  className?: string
}) {
  return (
    <ol className={cn('flex flex-col gap-5', className)}>
      {steps.map((step) => {
        const current = step.status === 'IN_PROGRESS'
        return (
          <li
            key={step.label}
            aria-current={current ? 'step' : undefined}
            className={cn(
              'flex items-center gap-3 text-sm font-semibold',
              current ? 'text-ink' : 'text-ink-600'
            )}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              <StepMarker step={step} />
            </span>
            {step.label}
          </li>
        )
      })}
    </ol>
  )
}

/** The steps in one line, where there is no room for the list beside the chat. */
export function InsuranceApplicationStepLine({
  steps,
  className,
}: {
  steps: ReadonlyArray<ApplicationStep>
  className?: string
}) {
  if (steps.length === 0) return null
  const index = currentStepIndex(steps)

  return (
    <div className={cn('flex items-baseline justify-between gap-4', className)}>
      <span className="text-ink truncate text-sm font-semibold">{steps[index].label}</span>
      <span className="text-ink-400 shrink-0 text-xs">
        Step {index + 1} of {steps.length}
      </span>
    </div>
  )
}

/** A hairline across the page, filled to the application's percent complete. */
export function InsuranceApplicationProgressBar({
  percent,
  className,
}: {
  percent: number
  className?: string
}) {
  return (
    <Progress
      value={percent}
      aria-label="Application progress"
      className={cn('bg-ink-50 h-1 shrink-0 rounded-none', className)}
      indicatorClassName="bg-ink-700"
    />
  )
}
